import { ApiError, type ApiClient } from "../api/client";
import { t } from "../i18n";

/** What the server answered about the pairing this browser kept. */
export type KeptPairing =
  | { name: "kept"; deviceToken: string }
  | { name: "none" }
  /** The server could not be asked: the pairing may well be kept, so no pairing screen yet. */
  | { name: "unreachable"; message: string };

/**
 * The pairing this browser kept on the server's side, in a cookie (Furkan, 13/10/2026: "op hun beginscherm
 * als app toevoegen vraagt die nog eens de 8-tekenige code, en soms logt die uit"; ADR 0044). An iPhone or
 * iPad empties the storage of a page after a week without a visit, and the app on the home screen starts
 * with an empty storage of its own; the cookie stays, and iOS copies it to the app on the home screen. So a
 * till or app that starts without its device token asks for it back before it shows the pairing screen.
 *
 * When the server cannot be reached the answer is "unreachable", not "none": on a bad connection the
 * pairing screen would ask for a new code although the pairing is kept, and a new code makes a second
 * device. Pairing needs the server anyway.
 */
export async function keptPairing(api: ApiClient, purpose: "till" | "app"): Promise<KeptPairing> {
  try {
    const answer = await api.request<{ deviceToken: string | null }>("POST", "/device/pairing/kept", {
      auth: "none",
      body: { purpose },
    });
    return answer.deviceToken === null ? { name: "none" } : { name: "kept", deviceToken: answer.deviceToken };
  } catch (error) {
    // a server from before 13/10/2026 does not know the question: nothing kept
    if (error instanceof ApiError && error.status === 404) return { name: "none" };
    return { name: "unreachable", message: error instanceof ApiError ? error.message : t.errors.unexpected };
  }
}
